import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  Snackbar,
  Paper,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';
import TwoFactorSetup from '../components/TwoFactorSetup';

const Profile: React.FC = () => {
  const { user, login } = useAuth();
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [profileErrors, setProfileErrors] = useState<any>({});
  const [passwordErrors, setPasswordErrors] = useState<any>({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Aggiorna i dati del profilo se l'oggetto 'user' dal contesto cambia
  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
      });
    }
  }, [user]);

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileData({ ...profileData, [name]: value });
    if (profileErrors[name]) {
      setProfileErrors({ ...profileErrors, [name]: '' });
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });
    if (passwordErrors[name]) {
      setPasswordErrors({ ...passwordErrors, [name]: '' });
    }
  };

  const validateProfile = () => {
    const errors: any = {};
    if (!profileData.name) {
      errors.name = 'Il nome è obbligatorio';
    }
    if (!profileData.email) {
      errors.email = 'L\'email è obbligatoria';
    } else if (!/\S+@\S+\.\S+/.test(profileData.email)) {
      errors.email = 'Formato email non valido';
    }
    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePassword = () => {
    const errors: any = {};
    if (!passwordData.currentPassword) {
      errors.currentPassword = 'La password attuale è obbligatoria';
    }
    if (!passwordData.newPassword) {
      errors.newPassword = 'La nuova password è obbligatoria';
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = 'La password deve essere di almeno 8 caratteri';
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Le password non coincidono';
    }
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProfileSubmit = async () => {
    if (!validateProfile() || !user) return;

    try {
      await userService.updateUser(user.id, {
        name: profileData.name,
        email: profileData.email,
      });
      showSnackbar('Profilo aggiornato con successo', 'success');
      // Potresti voler aggiornare lo stato 'user' nel contesto qui
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || 'Errore durante l\'aggiornamento del profilo',
        'error'
      );
    }
  };

  const handlePasswordSubmit = async () => {
    if (!validatePassword() || !user) return;

    try {
      await userService.changePassword(
        user.id,
        passwordData.currentPassword,
        passwordData.newPassword
      );
      showSnackbar('Password cambiata con successo', 'success');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || 'Errore durante il cambio password',
        'error'
      );
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Mio Profilo
      </Typography>

      <Grid container spacing={3}>
        {/* Card Informazioni Profilo (invariata) */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informazioni del Profilo
              </Typography>
              <Box sx={{ mt: 2 }}>
                <TextField fullWidth label="Nome" name="name" value={profileData.name} onChange={handleProfileChange} error={!!profileErrors.name} helperText={profileErrors.name} margin="normal" />
                <TextField fullWidth label="Email" name="email" type="email" value={profileData.email} onChange={handleProfileChange} error={!!profileErrors.email} helperText={profileErrors.email} margin="normal" />
                <Button variant="contained" fullWidth sx={{ mt: 3 }} onClick={handleProfileSubmit}>
                  Aggiorna Profilo
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Card Cambio Password (invariata) */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cambia Password
              </Typography>
              <Box sx={{ mt: 2 }}>
                <TextField fullWidth label="Password Attuale" name="currentPassword" type="password" value={passwordData.currentPassword} onChange={handlePasswordChange} error={!!passwordErrors.currentPassword} helperText={passwordErrors.currentPassword} margin="normal" />
                <TextField fullWidth label="Nuova Password" name="newPassword" type="password" value={passwordData.newPassword} onChange={handlePasswordChange} error={!!passwordErrors.newPassword} helperText={passwordErrors.newPassword} margin="normal" />
                <TextField fullWidth label="Conferma Nuova Password" name="confirmPassword" type="password" value={passwordData.confirmPassword} onChange={handlePasswordChange} error={!!passwordErrors.confirmPassword} helperText={passwordErrors.confirmPassword} margin="normal" />
                <Button variant="contained" fullWidth sx={{ mt: 3 }} onClick={handlePasswordSubmit}>
                  Cambia Password
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* --- SEZIONE 2FA AGGIUNTA QUI --- */}
        <Grid item xs={12}>
          {user && !user.is_2fa_enabled && (
            <TwoFactorSetup />
          )}
          {user && user.is_2fa_enabled && (
            <Alert severity="info" sx={{ mt: 2 }}>
              L'autenticazione a due fattori è attiva per questo account.
            </Alert>
          )}
        </Grid>
      </Grid>

      {/* Snackbar per le notifiche (invariata) */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile;